import React, {
    ReactElement,
    useState,
} from "react";
import { Form } from "react-bootstrap";
import {
    GetPortalSettingsDocument, Permission, PortalIndexProvider,
    useGetPortalSettingsQuery, useSavePortalSettingsMutation,
} from "../../../../__generated__/api";
import { Loading } from "../../../../components";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";
import Editor from "../../../../components/editor/Editor";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";
import RequestTypes from "../../../../components/pickers/request-types/RequestTypes";
import Select from "../../../../components/select/Select";
import useArticleContext from "../../../../contexts/HelpContext";
import usePortal from "../../../../contexts/PortalContext";
import { ErrorRemote } from "../../../error/Error";
import CodeBlock from "../../../../components/code-block/CodeBlock";
import Styles from "../../Administrator.module.scss";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import CreateToken from "../../../../components/create-token/CreateToken";

const PORTAL_INDEXES = [
    {
        label: "Open AI",
        value: PortalIndexProvider.OpenAi,
    },
    {
        label: "None",
        value: PortalIndexProvider.None,
    },
];

const FEATURE_SHOW_SSO = "frontend.sso.options";
const FEATURE_SHOW_SCIM = "frontend.scim.options";

export default function PortalSettings(): ReactElement {
    useArticleContext("urn:fq:article:8d394cb07a3a4b5395ebe1559171d705");
    const { featureFlags } = usePortal();

    const { data, error, loading: isLoading } = useGetPortalSettingsQuery();
    const [save] = useSavePortalSettingsMutation();

    const [title, setTitle] = useState(() => data?.portal?.title ?? "");
    const [welcomeMessage, setWelcomeMessage] = useState(() => data?.portal?.welcomeMessage ?? "<p></p>");
    const [publicPermissions, setPublicPermissions] = useState(() => data?.portal?.publicPermissions ?? []);
    const [guestPermissions, setGuestPermissions] = useState(() => data?.portal?.guestPermissions ?? []);
    const [publicFormIds, setPublicFormIds] = useState(data?.portal?.publicFormIds ?? []);
    const [guestFormIds, setGuestFormIds] = useState(data?.portal?.guestFormIds ?? []);
    const [isSharedChannelGuest, setIsSharedChannelGuest] = useState(data?.portal?.isSharedChannelGuest ?? false);

    const [isSingleSignOnEnabled, setIsSingleSignOnEnabled] = useState(data?.portal?.singleSignOn?.isEnabled ?? false);
    const [ssoLoginRedirectUrl, setSsoLoginRedirectUrl] = useState(data?.portal?.singleSignOn?.loginRedirectUrl);
    const [ssoIdentityProviderMetadata, setSsoIdentityProviderMetadata] = useState(
        data?.portal?.singleSignOn?.identityProviderMetadata,
    );

    const [scimAdministratorGroupId, setsScimAdministratorGroupId] = useState(data?.portal?.userProvisioning?.administratorGroupId);

    const [
        portalIndexProvider,
        setPortalIndexProvider,
    ] = useState(() => data?.portal?.portalIndexProvider ?? PortalIndexProvider.None);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Portal settings saved!",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    refetchQueries: [
                        { query: GetPortalSettingsDocument },
                    ],
                    variables: {
                        portal: {
                            title,
                            welcomeMessage,
                            publicPermissions,
                            guestPermissions,
                            publicFormIds,
                            guestFormIds,
                            portalIndexProvider,
                            isSharedChannelGuest,
                            singleSignOn: {
                                isEnabled: isSingleSignOnEnabled,
                                loginRedirectUrl: ssoLoginRedirectUrl,
                                identityProviderMetadata: ssoIdentityProviderMetadata,
                            },
                            userProvisioning: {
                                administratorGroupId: scimAdministratorGroupId,
                            },
                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchProperty(title, data?.portal?.title ?? "");
    dirtyTracker.useWatchProperty(welcomeMessage, data?.portal?.welcomeMessage ?? "<p></p>");
    dirtyTracker.useWatchProperty(publicPermissions.join(), (data?.portal?.publicPermissions ?? []).join());
    dirtyTracker.useWatchProperty(guestPermissions.join(), (data?.portal?.guestPermissions ?? []).join());
    dirtyTracker.useWatchProperty(publicFormIds.join(), (data?.portal?.publicFormIds ?? []).join());
    dirtyTracker.useWatchProperty(guestFormIds.join(), (data?.portal?.guestFormIds ?? []).join());
    dirtyTracker.useWatchProperty(
        portalIndexProvider ?? PortalIndexProvider.None,
        data?.portal?.portalIndexProvider ?? PortalIndexProvider.None,
    );
    dirtyTracker.useWatchProperty(isSharedChannelGuest ?? false, data?.portal?.isSharedChannelGuest ?? false);

    dirtyTracker.useWatchProperty(isSingleSignOnEnabled ?? false, data?.portal?.singleSignOn?.isEnabled ?? false);
    dirtyTracker.useWatchProperty(ssoLoginRedirectUrl, data?.portal?.singleSignOn?.loginRedirectUrl);
    dirtyTracker.useWatchProperty(ssoIdentityProviderMetadata, data?.portal?.singleSignOn?.identityProviderMetadata);
    dirtyTracker.useWatchProperty(scimAdministratorGroupId, data?.portal?.userProvisioning?.administratorGroupId);

    dirtyTracker.useLoad(() => {
        setTitle(data?.portal?.title ?? "");
        setWelcomeMessage(data?.portal?.welcomeMessage ?? "<p></p>");
        setPublicPermissions(data?.portal?.publicPermissions ?? []);
        setGuestPermissions(data?.portal?.guestPermissions ?? []);

        setPublicFormIds(data?.portal?.publicFormIds ?? []);
        setGuestFormIds(data?.portal?.guestFormIds ?? []);

        setPortalIndexProvider(data?.portal?.portalIndexProvider ?? PortalIndexProvider.None);
        setIsSharedChannelGuest(data?.portal?.isSharedChannelGuest ?? false);

        setIsSingleSignOnEnabled(data?.portal?.singleSignOn?.isEnabled ?? false);
        setSsoLoginRedirectUrl(data?.portal?.singleSignOn?.loginRedirectUrl);
        setSsoIdentityProviderMetadata(data?.portal?.singleSignOn?.identityProviderMetadata);
        setsScimAdministratorGroupId(data?.portal?.userProvisioning?.administratorGroupId);
    }, [data?.portal]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    const createListModifier = (
        current: Permission[],
        onChange: (permissions: Permission[]) => void,
        ...permission: Permission[]
    ) => (value: boolean) => {
        let copy = Array.from(current ?? []);
        if (value) {
            copy = copy.concat(permission);
        } else {
            const set = new Set(permission);
            copy = copy.filter(item => !set.has(item));
        }
        onChange(copy.dedupe());
    };

    return (
        <form className="flex-grow-1">

            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light">
                    General Settings
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Title</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="My Portal"
                        required
                        onChange={evt => setTitle(evt.target.value)}
                        value={title as string}
                    />
                    <Form.Text>
                        The title of the portal displayed to all users that visit the site.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Welcome message</Form.Label>

                    <Editor
                        className="border border-gray rounded"
                        text={welcomeMessage}
                        onChange={welcome => setWelcomeMessage(welcome)}
                        placeholder={`Welcome to ${title ?? "Our site"}`}
                    />
                    <Form.Text>
                        The first message that a user sees when visiting the portal.
                    </Form.Text>
                </Form.Group>

                <Form.Group>
                    <Form.Label className="fw-bolder">Search Index Provider</Form.Label>

                    <Select
                        className="text-gray"
                        placeholder=""
                        onChange={option => setPortalIndexProvider(option?.value as PortalIndexProvider)}
                        options={PORTAL_INDEXES}
                        value={PORTAL_INDEXES.find(option => option.value === portalIndexProvider)}
                    />
                    <Form.Text>
                        The Search Indexer is provided by a 3rd party service, OpenAI, LLC. View their
                        {" "}
                        <a href="https://openai.com/api/policies/privacy/" target="_blank" rel="noreferrer">Privacy Policy</a>
                        {" "}
                        to understand how Open AI handles data.
                    </Form.Text>

                </Form.Group>

            </div>

            <div className="border p-3 rounded mb-4 bg-white">
                <h5 className="mb-3 text-light ">
                    Guest Permissions
                </h5>
                <p>
                    Guests are users who have access to Slack but are not Agents or Administrators. By default,
                    all users who have access to your applications such as Slack or Microsoft Teams are guests.
                </p>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Read Articles</Form.Label>
                    {guestPermissions?.includes(Permission.ArticleReadPublic) && (
                        <div className={guestPermissions?.includes(Permission.ArticleReadPublic) ? "alert alert-info" : ""}>
                            <Form.Text className="text-primary">
                                All published articles are public and visible to everyone who is able to login
                                to this Portal as a guest.
                            </Form.Text>
                        </div>
                    )}

                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={guestPermissions?.includes(Permission.ArticleReadPublic) ?? false}
                        onChange={createListModifier(guestPermissions, setGuestPermissions, Permission.ArticleReadPublic)}
                    />
                    {!guestPermissions?.includes(Permission.ArticleReadPublic) && (
                        <Form.Text>
                            If enabled, all published articles become available to any guest that has access to Slack.
                            All accounts and guest accounts who have access to your Slack
                            workspace will have access to these articles
                        </Form.Text>
                    )}

                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Create tickets</Form.Label>

                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={guestPermissions?.includes(Permission.TicketCreateGuest) ?? false}
                        onChange={createListModifier(
                            guestPermissions,
                            setGuestPermissions,
                            Permission.TicketCreateGuest,
                            Permission.TicketsReadSelf,
                        )}
                    />
                    <Form.Text>
                        If enabled guests will be able to create tickets as themselves.
                    </Form.Text>

                </Form.Group>

                {guestPermissions?.includes(Permission.TicketCreateGuest) && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Request Types</Form.Label>

                        <RequestTypes
                            isMulti
                            placeholder="The request types that guests can create."
                            onSelected={selected => setGuestFormIds(selected)}
                            value={guestFormIds as string[]}
                        />
                        <Form.Text>
                            Specify which request types guest users are able to create.
                            To order the request types, click and drag them inside this box.
                        </Form.Text>

                    </Form.Group>
                )}

                {guestPermissions?.includes(Permission.ArticleReadPublic) && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Slack Connect users are guests</Form.Label>

                        <BooleanSelect
                            value={isSharedChannelGuest}
                            onChange={value => setIsSharedChannelGuest(value ?? false)}
                        />
                        <Form.Text>
                            If yes, users coming from indirect sources such as Slack Connect channels will
                            be considered guests and will be able to view articles and create tickets.
                        </Form.Text>

                    </Form.Group>
                )}

            </div>
            <div className="border p-3 rounded mb-5 bg-white">
                <h5 className="mb-3 text-light ">
                    Public Permissions
                </h5>
                <p>
                    Public permissions control what unauthenticated people on the internet
                    are able to view.
                </p>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">All Articles are Public</Form.Label>
                    {publicPermissions?.includes(Permission.ArticleReadPublic) && (
                        <div className={publicPermissions?.includes(Permission.ArticleReadPublic) ? "alert alert-danger" : ""}>
                            <Form.Text className="text-primary">
                                All published articles are public and visible to everyone on the internet.
                            </Form.Text>
                        </div>
                    )}

                    <BooleanSelect
                        className="text-gray"
                        isRequired
                        value={publicPermissions?.includes(Permission.ArticleReadPublic) ?? false}
                        onChange={createListModifier(publicPermissions, setPublicPermissions, Permission.ArticleReadPublic)}
                    />
                    {!publicPermissions?.includes(Permission.ArticleReadPublic) && (
                        <Form.Text className="text-danger">
                            If enabled, all published articles become public to the internet.
                            Everyone in the world will be allowed to read all articles.
                        </Form.Text>
                    )}

                </Form.Group>

            </div>

            {featureFlags.has(FEATURE_SHOW_SSO) && (

                <div className="border p-3 rounded mb-4 bg-white">
                    <h5 className="mb-3 text-light ">
                        Single Sign On
                    </h5>
                    <p>
                        Single sign on allows users to sign into the Foqal portal using your Identity provider such as
                        Okta, Azure, or One Login
                    </p>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Enabled</Form.Label>
                        <BooleanSelect
                            className="text-gray"
                            isRequired
                            value={isSingleSignOnEnabled}
                            onChange={value => setIsSingleSignOnEnabled(value ?? false)}
                        />

                    </Form.Group>
                    {isSingleSignOnEnabled && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder">Sign In URL</Form.Label>
                                <Form.Control
                                    type="url"
                                    placeholder="https://my-site.myidp.com"
                                    required
                                    onChange={evt => setSsoLoginRedirectUrl(evt.target.value)}
                                    value={ssoLoginRedirectUrl as string}
                                />
                                <Form.Text>
                                    What URL to redirect the user to request authentication
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder d-block">Provider Sign in Url</Form.Label>
                                <Form.Text>
                                    Configure your SSO provider to use this URL for sign in.
                                </Form.Text>
                                <CodeBlock
                                    shouldWrap
                                    isCopyable
                                    code={data?.portal?.singleSignOn?.providerLoginUrl as string}
                                />

                            </Form.Group>

                            {data?.portal?.singleSignOn?.identityProviderMetadata && ssoIdentityProviderMetadata ? (
                                <div className="mb-3">
                                    <Form.Label className="fw-bolder d-block">Identity Provider Metadata</Form.Label>
                                    <Form.Text>
                                        You currently have an identity metadata file uploaded. To upload a different file,
                                        click below to remove the current identity file, then select a new file, and save.
                                    </Form.Text>

                                    <button
                                        className="btn btn-danger d-block my-2"
                                        type="button"
                                        onClick={() => setSsoIdentityProviderMetadata("")}
                                    >
                                        Remove Identity File
                                    </button>
                                </div>
                            ) : (
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bolder">Identity Provider Metadata</Form.Label>
                                    <Form.Control
                                        type="text"
                                        as="textarea"
                                        placeholder={"<?xml version=\"1.0\"?>\n<EntityDescriptor.."}
                                        required
                                        className={Styles["portal-certificate-box"]}
                                        onChange={evt => setSsoIdentityProviderMetadata(evt.target.value)}
                                        value={ssoIdentityProviderMetadata as string}
                                    />
                                    <Form.Text>
                                        What URL to redirect the user to request authentication
                                    </Form.Text>
                                </Form.Group>
                            )}

                        </>
                    )}

                </div>
            )}

            {featureFlags.has(FEATURE_SHOW_SCIM) && (

                <div className="border p-3 rounded mb-4 bg-white">
                    <h5 className="mb-3 text-light ">
                        User Provisioning (SCIM)
                    </h5>
                    <p>
                        User Provisioning allows you to setup up groups in your Identity Provider which
                        synchronize into Foqal Agent to automatically provision users as Guests, Agents, or
                        Administrators.
                    </p>

                    <div className="mb-3">
                        <Form.Label className="fw-bolder d-block">Administrator Group</Form.Label>
                        <UserGroups
                            value={scimAdministratorGroupId}
                            onSelected={value => setsScimAdministratorGroupId(value)}
                        />

                        <Form.Text>
                            Any users that is added to this group will be automaticaly provisioned as an Administrator.
                            All other users provisoned to other groups will be provisioned as agents.
                        </Form.Text>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder d-block">SCIM URL</Form.Label>
                        <Form.Text>
                            Use this to configure your SCIM server url.
                        </Form.Text>
                        <CodeBlock
                            shouldWrap
                            isCopyable
                            code={data?.portal?.userProvisioning?.providerSCIMUrl as string}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder d-block">SCIM Token</Form.Label>
                        <Form.Text>
                            Use this token when asked for the SCIM authentication token.
                        </Form.Text>
                        <CreateToken
                            name="SCIM Token"
                            permissions={[
                                Permission.GroupsRead, Permission.GroupsWrite,
                                Permission.UsersRead, Permission.UsersWrite,
                            ]}
                        />
                    </Form.Group>

                </div>
            )}

            {dirtyTracker.saveComponent}

        </form>
    );
}
